import storageActions from "app/utils/storageActions";
import store from "app/redux/store";
import { update as updateValueAction } from "app/actions/values";

const QUEUE_KEY = "offline_queue";
let isFlushing = false;

const offlineQueue = {
  isFlushing() {
    return isFlushing;
  },

  async getQueue() {
    return await storageActions.fetchItem(QUEUE_KEY, []);
  },

  async enqueue(item) {
    const queue = await offlineQueue.getQueue();
    // Deduplicate by model+action+data
    const key = `${item.model}|${item.action}|${JSON.stringify(item.data)}`;
    if (queue.some((q) => `${q.model}|${q.action}|${JSON.stringify(q.data)}` === key)) return;
    const entry = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      timestamp: new Date().toISOString(),
      ...item,
    };
    queue.push(entry);
    await storageActions.saveItem(QUEUE_KEY, queue);
    store.dispatch({ type: "app/set_offline_queue", queue });
  },

  async dequeue(id) {
    let queue = await offlineQueue.getQueue();
    queue = queue.filter((item) => item.id !== id);
    await storageActions.saveItem(QUEUE_KEY, queue);
    store.dispatch({ type: "app/set_offline_queue", queue });
  },

  async flush() {
    if (isFlushing) return;
    const queue = await offlineQueue.getQueue();
    if (!queue.length) return;
    isFlushing = true;
    const tempIdMap = {}; // maps tempId string → real id
    try {
      // Dynamic require to avoid circular dependency
      const apiActions = require("app/utils/apiActions").default;
      for (const item of [...queue]) {
        // Substitute any resolved temp IDs into the model path and data
        let model = item.model;
        let data = item.data;
        for (const [tempId, realId] of Object.entries(tempIdMap)) {
          model = model.split(tempId).join(String(realId));
          if (data) {
            data = JSON.parse(JSON.stringify(data).split(tempId).join(String(realId)));
          }
        }

        const result = await apiActions.request(model, item.action, data, 0, item.multipart);
        if (result === null) {
          // null = network error sentinel, stop flushing and go back offline
          offlineQueue.markOffline();
          break;
        }
        if (result?.conflict) {
          if (offlineQueue._autoResolveIfExtension(item, result, apiActions)) {
            await offlineQueue.dequeue(item.id);
            continue;
          }
          store.dispatch({
            type: "app/add_conflict",
            conflict: {
              id: item.id,
              description: item.description,
              localData: item.data,
              serverValue: result.value,
            },
          });
        }
        if (item.tempId && result?.value) {
          tempIdMap[item.tempId] = result.value.id;
          store.dispatch({ type: "values/replace_temp", tempId: item.tempId, value: result.value });
        }
        await offlineQueue.dequeue(item.id);
      }
    } finally {
      isFlushing = false;
    }
  },

  async clearQueue() {
    await storageActions.saveItem(QUEUE_KEY, []);
    store.dispatch({ type: "app/set_offline_queue", queue: [] });
  },

  async loadIntoRedux() {
    const queue = await offlineQueue.getQueue();
    store.dispatch({ type: "app/set_offline_queue", queue });
    // If we're already online and have pending items (e.g. from a previous session),
    // attempt to flush them immediately.
    const { app } = store.getState();
    if (app.isOnline !== false && queue.length > 0) {
      offlineQueue.flush();
    }
  },

  markOffline() {
    const { app } = store.getState();
    if (app.isOnline !== false) {
      store.dispatch({ type: "app/set_online", isOnline: false });
    }
  },

  markOnline() {
    const { app } = store.getState();
    if (app.isOnline !== true) {
      store.dispatch({ type: "app/set_online", isOnline: true });
      offlineQueue.flush();
    }
  },

  // Returns true and auto-resolves if the conflict is just one side having more text.
  _autoResolveIfExtension(item, result, apiActions) {
    const localValue = item.data?.value || {};
    const serverValue = result.value || {};
    const TEXT_FIELDS = ['text', 'title'];

    // Only consider fields the local update actually touched
    const touchedFields = TEXT_FIELDS.filter((f) => localValue[f] !== undefined);
    if (!touchedFields.length) return false;

    const isExtension = (a, b) => {
      if (typeof a !== 'string' || typeof b !== 'string') return a === b;
      const longer = a.length >= b.length ? a : b;
      const shorter = a.length < b.length ? a : b;
      return longer.startsWith(shorter);
    };

    const allExtensions = touchedFields.every((f) =>
      isExtension(localValue[f] ?? '', serverValue[f] ?? '')
    );
    if (!allExtensions) return false;

    // Build merged value: take the longer side for each field
    const merged = { ...serverValue };
    let localWins = false;
    for (const f of touchedFields) {
      const local = localValue[f] ?? '';
      const server = serverValue[f] ?? '';
      if (local.length >= server.length) {
        merged[f] = local;
        if (local !== server) localWins = true;
      }
    }

    // Update the local Redux store immediately
    store.dispatch(updateValueAction(merged));

    // If local had more text, re-submit to the server using the server's timestamp
    // to bypass the conflict check on the next save
    if (localWins) {
      apiActions.request(item.model, item.action, {
        ...item.data,
        value: { ...localValue, ...Object.fromEntries(touchedFields.map((f) => [f, merged[f]])) },
        client_updated_at: serverValue.updated_at,
      });
    }

    return true;
  },

  describe(model, action, data) {
    const parts = (model || "").split("/");
    const resource = parts[0];
    const subaction = parts[2];
    const resourceLabels = {
      values: "value",
      moments: "moment",
      chapters: "chapter",
      users: "user",
    };
    const label = resourceLabels[resource] || resource;

    const rawName = data?.value?.title || data?.value?.text?.split("\n")[0]?.trim();
    const name = rawName ? `"${rawName.slice(0, 40)}${rawName.length > 40 ? "…" : ""}"` : null;

    if (subaction) {
      const actionLabels = {
        add_parent: "Add parent to",
        remove_parent: "Remove parent from",
        archive_parent: "Archive parent of",
        add_child: "Add child to",
        remove_child: "Remove child from",
        archive_child: "Archive child of",
        update_completed_at: "Complete",
        progress: "Add progress to",
        toggle_parent: "Toggle parent of",
        update_all_revisions: "Update revisions of",
      };
      const verb = actionLabels[subaction] || subaction.replace(/_/g, " ");
      return name ? `${verb} ${name}` : verb;
    }
    if (action === "POST") return name ? `Create ${label} ${name}` : `Create ${label}`;
    if (action === "PATCH" || action === "PUT") return name ? `Update ${name}` : `Update ${label}`;
    if (action === "DELETE") return name ? `Delete ${name}` : `Delete ${label}`;
    return `${action} ${resource}`;
  },
};

export default offlineQueue;
